import {
  BudgetItemsQuery,
  CostItemEntryType,
  CostItemStatus,
  ExpenseFragmentFragment,
  ExpenseStatus,

  useBudgetItemsQuery,
} from 'gql/index';
import { useCallback, useMemo, useState } from 'react';
import { BudgetTableRow, CostRow, ExpenseRow, QuoteRow } from '../types';

type BudgetItem = BudgetItemsQuery['budgetItems'][number];
type CostItem = BudgetItem['costItems'][number];
type Entry = CostItem['entries'][number];
type Quote = CostItem['entries'][number]['quote'];

function buildExpenseRows(
  expenseEntries: Array<Entry & { expense?: ExpenseFragmentFragment | null }>,
  budgetItem: BudgetItem,
  costItem: CostItem,
  quoteIdToEntryId: Map<number, number>,
): ExpenseRow[] {
  return expenseEntries.map(entry => {
    const expense = entry.expense;
    if (!expense) return null;
    const underQuoteId = expense.quoteId ?? null;
    const quoteEntryId = underQuoteId != null ? quoteIdToEntryId.get(underQuoteId) : undefined;
    const treePath = quoteEntryId != null
      ? [budgetItem.id.toString(), costItem.id.toString(), quoteEntryId.toString(), entry.id.toString()]
      : [budgetItem.id.toString(), costItem.id.toString(), entry.id.toString()];
    const expendedNum = Number(expense.expendedAmount);
    const noQuoteQuoted = underQuoteId == null ? expendedNum : undefined;
    return {
      id: `expense-${expense.id}`,
      __reorder__: expense.name,
      type: 'expense',
      originalId: expense.id,
      name: expense.name,
      quoteId: expense.quoteId ?? undefined,
      order: entry.order,
      planned: 0,
      spent: expense.status === ExpenseStatus.Paid ? expendedNum : 0,
      toPayAmount: expense.status === ExpenseStatus.NotPaid ? expendedNum : 0,
      effectiveAmount: expendedNum,
      quotedAmount: noQuoteQuoted,
      treePath,
      plannedDate: expense.expenseDate,
      budgetItemId: budgetItem.id,
      costItemId: costItem.id,
      status: expense.status,
      expense,
      entryId: entry.id,
    } satisfies ExpenseRow & { __reorder__: string };
  }).filter(row => row != null);
}

function buildQuoteRows(
  quoteEntries: Array<Entry & { quote?: Quote | null }>,
  budgetItem: BudgetItem,
  costItem: CostItem,
  expenseRows: ExpenseRow[],
): QuoteRow[] {
  return quoteEntries.map(entry => {
    const quote = entry.quote;
    if (!quote) return null;
    const expensesUnderQuote = expenseRows.filter(e => e.quoteId === quote.id);
    const paidUnderQuote = expensesUnderQuote
      .filter(e => e.status === 'Paid')
      .reduce((acc, e) => acc + (e.spent ?? 0), 0);
    const quotedNum = Number(quote.amount);
    const toPay = Math.max(0, quotedNum - paidUnderQuote);

    return {
      id: `quote-${quote.id}`,
      __reorder__: quote.name,
      type: 'quote',
      originalId: quote.id,
      name: quote.name,
      order: entry.order,
      planned: 0,
      quotedAmount: quote.amount,
      spent: paidUnderQuote,
      toPayAmount: toPay,
      effectiveAmount: Math.max(quote.amount, paidUnderQuote),
      treePath: [budgetItem.id.toString(), costItem.id.toString(), entry.id.toString()],
      plannedDate: quote.quoteDate,
      budgetItemId: budgetItem.id,
      costItemId: costItem.id,
      entryId: entry.id,
      quote: {
        id: quote.id,
        name: quote.name,
        amount: Number(quote.amount),
        quoteDate: quote.quoteDate,
        attachments: quote.attachments ?? [],
      },
    } satisfies QuoteRow & { __reorder__: string };
  }).filter(row => row != null);
}

function buildCostRow(
  costItem: CostItem,
  budgetItem: BudgetItem,
  expenseRows: ExpenseRow[],
  quoteRows: QuoteRow[],
  sortedEntriesLength: number,
): CostRow {
  console.log('expenseRows', expenseRows);
  const paidActual = expenseRows
    .filter(e => e.status === 'Paid')
    .reduce((acc, e) => acc + (e.spent ?? 0), 0);
  const quotedAmountFromQuotes = quoteRows.reduce((acc, q) => acc + (q.quotedAmount ?? 0), 0);
  const implicitQuotedAmountFromExpensesWithoutQuotes = expenseRows
    .filter(e => e.quoteId == null)
    .reduce((acc, e) => acc + Number(e.expense.expendedAmount ?? 0), 0);
  const quotedAmount = quotedAmountFromQuotes + implicitQuotedAmountFromExpensesWithoutQuotes;
  const toPayAmount = Math.max(0, quotedAmount - paidActual);

  return {
    id: `cost-${costItem.id}`,
    __reorder__: costItem.name,
    type: 'cost',
    originalId: costItem.id,
    name: costItem.name,
    order: costItem.order,
    planned: costItem.plannedAmount,
    spent: paidActual,
    effectiveAmount: costItem.effectiveAmount,
    quotedAmount,
    toPayAmount,
    treePath: [budgetItem.id.toString(), costItem.id.toString()],
    budgetItemId: budgetItem.id,
    costItemId: costItem.id,
    status: costItem.status,
    hasEntries: sortedEntriesLength > 0,
  };
}

function buildEntryRowsUnderCostItem(
  quoteEntries: Array<Entry & { quote?: Quote | null }>,
  expenseRows: ExpenseRow[],
  quoteRows: QuoteRow[],
): BudgetTableRow[] {
  const expensesUnderQuote = (quoteId: number) =>
    expenseRows.filter(e => e.expense.quoteId === quoteId).sort((a, b) => a.order - b.order);
  const expensesWithoutQuote = expenseRows
    .filter(e => (e.expense.quoteId ?? null) == null)
    .sort((a, b) => a.order - b.order);

  const result: BudgetTableRow[] = [];
  quoteEntries.forEach(quoteEntry => {
    const quote = quoteEntry.quote;
    if (!quote) return;
    const quoteRow = quoteRows.find(r => r.originalId === quote.id);
    if (quoteRow) result.push(quoteRow);
    expensesUnderQuote(quote.id).forEach(er => result.push(er));
  });
  expensesWithoutQuote.forEach(er => result.push(er));
  return result;
}

function buildRowsForCostItem(
  costItem: CostItem,
  budgetItem: BudgetItem,
): BudgetTableRow[] {
  const sortedEntries = [...(costItem.entries ?? [])].sort((a, b) => a.order - b.order);
  const expenseEntries = sortedEntries.filter(
    entry => entry.type === CostItemEntryType.Expense && entry.expense != null,
  );
  const quoteEntries = sortedEntries.filter(
    entry => entry.type === CostItemEntryType.Quote && entry.quote != null,
  );

  const quoteIdToEntryId = new Map<number, number>();
  quoteEntries.forEach(entry => {
    if (entry.quote?.id != null) quoteIdToEntryId.set(entry.quote.id, entry.id);
  });

  const expenseRows = buildExpenseRows(expenseEntries, budgetItem, costItem, quoteIdToEntryId);
  const quoteRows = buildQuoteRows(quoteEntries, budgetItem, costItem, expenseRows);
  const costRow = buildCostRow(costItem, budgetItem, expenseRows, quoteRows, sortedEntries.length);
  const entryRows = buildEntryRowsUnderCostItem(quoteEntries, expenseRows, quoteRows);

  return [costRow, ...entryRows];
}

function buildBudgetRow(budgetItem: BudgetItem, costRows: BudgetTableRow[], draftCosts: CostRow[]) {
  const allCostRows = [...draftCosts, ...costRows];
  const actual = allCostRows
    .filter((row): row is CostRow => row.type === 'cost')
    .reduce((acc, c) => acc + (c.spent ?? 0), 0);
  const quotedAmount = allCostRows
    .filter((row): row is CostRow => row.type === 'cost')
    .reduce((acc, c) => acc + (c.quotedAmount ?? 0), 0);
  const toPayAmount = allCostRows
    .filter((row): row is CostRow => row.type === 'cost')
    .reduce((acc, c) => acc + (c.toPayAmount ?? 0), 0);
  const spent = allCostRows
    .filter((row): row is CostRow => row.type === 'cost')
    .reduce((acc, c) => acc + (c.spent ?? 0), 0);
  const effectiveAmount = allCostRows
    .filter((row): row is CostRow => row.type === 'cost')
    .reduce((acc, c) => acc + c.effectiveAmount, 0);

  return {
    id: `budget-${budgetItem.id}`,
    __reorder__: budgetItem.name,
    type: 'budget' as const,
    originalId: budgetItem.id,
    name: budgetItem.name,
    planned: budgetItem.budgetedAmount,
    order: budgetItem.order,
    actual,
    effectiveAmount,
    quotedAmount,
    toPayAmount,
    spent,
    treePath: [budgetItem.id.toString()],
    budgetItemId: budgetItem.id,
    status: undefined,
  };
}

function buildRowsFromBudgetItems(
  budgetItems: BudgetItem[],
  draftBudgetRows: BudgetTableRow[],
  draftCostRows: Record<number, CostRow[]>,
): BudgetTableRow[] {
  const orderedBudgetItems = [...budgetItems].sort((a, b) => a.order - b.order);

  const rowsForBudgetItems = orderedBudgetItems.flatMap<BudgetTableRow>(budgetItem => {
    const costItems = [...(budgetItem.costItems ?? [])].sort((a, b) => a.order - b.order);
    const draftCosts = draftCostRows[budgetItem.id] ?? [];
    const costRows = costItems.flatMap(costItem =>
      buildRowsForCostItem(costItem, budgetItem),
    ).sort((a, b) => a.order - b.order);
    const budgetRow = buildBudgetRow(budgetItem, costRows, draftCosts);
    return [budgetRow, ...draftCosts, ...costRows];
  });

  return [...draftBudgetRows, ...rowsForBudgetItems];
}

export function useBudgetRows(projectId: number | undefined) {
  const { data: budgetItems = [], isFetching } = useBudgetItemsQuery(
    { projectId: projectId ?? 0 },
    { select: d => d.budgetItems, enabled: projectId != null },
  );

  const [draftBudgetRows, setDraftBudgetRows] = useState<BudgetTableRow[]>([]);
  const [draftCostRows, setDraftCostRows] = useState<Record<number, CostRow[]>>({});

  const removeDraftBudgetRow = useCallback((draftId: string) => {
    setDraftBudgetRows(prev => prev.filter(row => row.id !== draftId));
  }, []);

  const removeDraftCostRow = useCallback((budgetId: number, draftId: string) => {
    setDraftCostRows(prev => {
      const current = prev[budgetId];
      if (!current) return prev;
      const updated = current.filter(row => row.id !== draftId);
      if (updated.length === 0) {
        const { [budgetId]: _, ...rest } = prev;
        return rest;
      }
      return { ...prev, [budgetId]: updated };
    });
  }, []);

  const createDraftId = useCallback(
    (prefix: string) => `${prefix}-${Date.now()}-${Math.round(Math.random() * 10000)}`,
    [],
  );

  const addBudgetDraft = useCallback((): string => {
    const draftId = createDraftId('draft-budget');
    const draftRow: BudgetTableRow = {
      id: draftId,
      type: 'budget',
      originalId: -1,
      name: '',
      order: -1,
      spent: 0,
      effectiveAmount: 0,
      treePath: [draftId],
      budgetItemId: -1,
      status: undefined,
    };
    setDraftBudgetRows(prev => [draftRow, ...prev]);
    return draftId;
  }, [createDraftId]);

  const addCostDraft = useCallback(
    (budgetId: number): string => {
      const draftId = createDraftId('draft-cost');
      const draftRow: CostRow = {
        id: draftId,
        __reorder__: '',
        type: 'cost',
        originalId: -1,
        name: '',
        order: -1,
        spent: 0,
        effectiveAmount: 0,
        hasEntries: false,
        treePath: [budgetId.toString(), draftId],
        budgetItemId: budgetId,
        costItemId: -1,
        status: CostItemStatus.Planned,
      };
      setDraftCostRows(prev => ({
        ...prev,
        [budgetId]: [draftRow, ...(prev[budgetId] ?? [])],
      }));
      return draftId;
    },
    [createDraftId],
  );

  const rows = useMemo<BudgetTableRow[]>(
    () => buildRowsFromBudgetItems(budgetItems, draftBudgetRows, draftCostRows),
    [budgetItems, draftBudgetRows, draftCostRows],
  );

  return {
    rows,
    budgetItems,
    isFetching,
    addBudgetDraft,
    addCostDraft,
    removeDraftBudgetRow,
    removeDraftCostRow,
  };
}
